import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Maximize2, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import { SANCTUARY_COMPONENTS, IMG } from "@/lib/site";

export default function SanctuaryMasterplan() {
  const [active, setActive] = useState(SANCTUARY_COMPONENTS[2]);
  const [zoom, setZoom] = useState(false);

  return (
    <div data-testid="sanctuary-masterplan-interactive">
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-hairline bg-white shadow-xl">
              <img
                src={IMG.masterplan}
                alt="Nseya Women & Children's Sanctuary conceptual master plan — an aerial illustration of a 4–5 hectare community with medical facility, housing, school, farm, water and solar systems"
                className="w-full"
                data-testid="sanctuary-masterplan-image"
              />
              {SANCTUARY_COMPONENTS.map((c) => (
                <button
                  key={c.n}
                  onClick={() => setActive(c)}
                  aria-label={`Zone ${c.n}: ${c.title}`}
                  data-testid={`sanctuary-hotspot-${c.n}`}
                  className={`absolute z-10 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 font-mono text-xs font-semibold shadow-md transition-all duration-200 hover:scale-110 ${
                    active.n === c.n ? "z-20 scale-110 border-gold bg-gold text-forest" : "border-white bg-forest/90 text-ivory"
                  }`}
                  style={{ left: `${c.pos.x}%`, top: `${c.pos.y}%` }}
                >
                  {active.n === c.n && <span className="absolute inset-0 animate-pulse-ring rounded-full bg-gold/60" aria-hidden="true" />}
                  {c.n}
                </button>
              ))}
              <div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-charcoal/60 to-transparent px-5 py-4">
                <p className="eyebrow text-gold">Conceptual Master Plan — Long-Term Vision</p>
              </div>
              <button
                onClick={() => setZoom(true)}
                className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 rounded-full bg-charcoal/80 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-ivory backdrop-blur transition-colors hover:bg-charcoal"
                data-testid="sanctuary-zoom-button"
              >
                <Maximize2 className="h-4 w-4" /> View full plan
              </button>
            </div>
          </Reveal>
          <p className="mt-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink" data-testid="sanctuary-masterplan-caption">
            <MapPin className="h-3.5 w-3.5 text-ochre" /> Tap a numbered zone to explore its purpose
          </p>
        </div>

        <div className="lg:col-span-4">
          <Reveal delay={0.1}>
            <div className="sticky top-28 rounded-2xl border border-hairline bg-sand p-8" data-testid="sanctuary-active-zone-card">
              <p className="eyebrow text-ochre">Zone {active.n} of 13</p>
              <h3 className="mt-3 font-display text-2xl font-semibold text-forest sm:text-3xl" data-testid="sanctuary-zone-title">
                {active.title}
              </h3>
              <p className="mt-4 leading-relaxed text-mutedink" data-testid="sanctuary-zone-desc">
                {active.desc}
              </p>
              <div className="mt-8 flex flex-wrap gap-2">
                {SANCTUARY_COMPONENTS.map((c) => (
                  <button
                    key={c.n}
                    onClick={() => setActive(c)}
                    data-testid={`sanctuary-zone-chip-${c.n}`}
                    aria-label={c.title}
                    className={`h-9 w-9 rounded-full border font-mono text-xs transition-all ${
                      active.n === c.n ? "border-gold bg-gold text-forest" : "border-forest/20 bg-white text-forest/70 hover:border-forest/50"
                    }`}
                  >
                    {c.n}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal/95 p-4"
            onClick={() => setZoom(false)}
            data-testid="sanctuary-lightbox"
          >
            <button className="absolute right-5 top-5 rounded-full bg-ivory/10 p-2 text-ivory hover:bg-ivory/20" aria-label="Close full plan" data-testid="sanctuary-lightbox-close-button">
              <X className="h-6 w-6" />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={IMG.masterplan}
              alt="Nseya Women & Children's Sanctuary conceptual master plan, full view"
              className="max-h-[88vh] w-auto max-w-full rounded-lg"
              onClick={(e) => e.stopPropagation()}
              data-testid="sanctuary-lightbox-image"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
